// ════════════════════════════════════════════════════════════════════════════
// LPA Wealth — Design Tokens (TypeScript export)
// Sprint Tech Debt #1 (audit P1-11 phase 1)
//
// Espelha src/shared/designTokens.css para uso programático.
// Use em componentes onde ainda há inline-styling (refactor incremental):
//
//   import { tokens } from "@/shared/tokens";
//   <div style={{ background: tokens.color.card, padding: tokens.sp[7] }}>
//
// Quando o componente migra para className + CSS, troca-se por var(--lpa-*).
// ════════════════════════════════════════════════════════════════════════════

export const tokens = {
  color: {
    // Brand (paleta copper oficial — DESIGN_SYSTEM.md v2.0)
    midnight: "var(--lpa-midnight)",
    obsidian: "var(--lpa-obsidian)",
    charcoal: "var(--lpa-charcoal)",
    copper: "var(--lpa-copper)",
    copperHover: "var(--lpa-copper-hover)",
    copperGlow: "var(--lpa-copper-glow)",
    platinum: "var(--lpa-platinum)",
    // Aliases legados
    navy: "var(--lpa-navy)",
    navy2: "var(--lpa-navy-2)",
    gold: "var(--lpa-gold)",
    emerald: "var(--lpa-emerald)",
    // Surfaces
    bg: "var(--lpa-bg)",
    card: "var(--lpa-card)",
    surface: "var(--lpa-surface)",
    overlay: "var(--lpa-overlay)",
    // Glass
    glassBg: "var(--lpa-glass-bg)",
    glassBorder: "var(--lpa-glass-border)",
    glassBlur: "var(--lpa-glass-blur)",
    glassShadow: "var(--lpa-glass-shadow)",
    // Text
    text: "var(--lpa-text)",
    sub: "var(--lpa-sub)",
    muted: "var(--lpa-muted)",
    dim: "var(--lpa-dim)",
    // Borders
    border: "var(--lpa-border)",
    borderStrong: "var(--lpa-border-strong)",
    // Accent
    accent: "var(--lpa-accent)",
    accentHover: "var(--lpa-accent-hover)",
    accentText: "var(--lpa-accent-text)",
    accentSoft: "var(--lpa-accent-soft)",
    focusRing: "var(--lpa-focus-ring)",
    // Semantic
    success: "var(--lpa-success)",
    successBg: "var(--lpa-success-bg)",
    danger: "var(--lpa-danger)",
    dangerBg: "var(--lpa-danger-bg)",
    warning: "var(--lpa-warning)",
    warningBg: "var(--lpa-warning-bg)",
    info: "var(--lpa-info)",
    infoBg: "var(--lpa-info-bg)",
  },
  font: {
    sans: "var(--lpa-font-sans)",
    mono: "var(--lpa-font-mono)",
    serif: "var(--lpa-font-serif)",
  },
  text: {
    "2xs": "var(--lpa-text-2xs)",
    xs: "var(--lpa-text-xs)",
    sm: "var(--lpa-text-sm)",
    md: "var(--lpa-text-md)",
    lg: "var(--lpa-text-lg)",
    xl: "var(--lpa-text-xl)",
    "2xl": "var(--lpa-text-2xl)",
    "3xl": "var(--lpa-text-3xl)",
    "4xl": "var(--lpa-text-4xl)",
    "5xl": "var(--lpa-text-5xl)",
  },
  sp: {
    0: "var(--lpa-sp-0)",
    1: "var(--lpa-sp-1)",
    2: "var(--lpa-sp-2)",
    3: "var(--lpa-sp-3)",
    4: "var(--lpa-sp-4)",
    5: "var(--lpa-sp-5)",
    6: "var(--lpa-sp-6)",
    7: "var(--lpa-sp-7)",
    8: "var(--lpa-sp-8)",
    9: "var(--lpa-sp-9)",
    10: "var(--lpa-sp-10)",
    12: "var(--lpa-sp-12)",
  },
  radius: {
    sm: "var(--lpa-radius-sm)",
    md: "var(--lpa-radius-md)",
    lg: "var(--lpa-radius-lg)",
    xl: "var(--lpa-radius-xl)",
    pill: "var(--lpa-radius-pill)",
  },
  shadow: {
    sm: "var(--lpa-shadow-sm)",
    md: "var(--lpa-shadow-md)",
    lg: "var(--lpa-shadow-lg)",
    xl: "var(--lpa-shadow-xl)",
  },
  tr: {
    fast: "var(--lpa-tr-fast)",
    base: "var(--lpa-tr-base)",
    slow: "var(--lpa-tr-slow)",
  },
  z: {
    dropdown: "var(--lpa-z-dropdown)",
    sticky: "var(--lpa-z-sticky)",
    fixed: "var(--lpa-z-fixed)",
    modal: "var(--lpa-z-modal)",
    toast: "var(--lpa-z-toast)",
  },
} as const;

/**
 * Set tema light/dark via atributo no <html>.
 * Persiste em localStorage.
 */
export function setTheme(theme: "light" | "dark"): void {
  try {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("lpa_theme", theme);
    localStorage.setItem("lpa_theme_user_set", "1");
  } catch {
    /* SSR/private mode */
  }
}

export function getStoredTheme(): "light" | "dark" {
  // Default = dark. O Admin shell (T={DARK}) sempre usa fundos escuros,
  // então CSS vars precisam estar em dark mode pra texto não sumir.
  // Usuário pode trocar via LpaThemeToggle e fica persistido.
  try {
    const v = localStorage.getItem("lpa_theme");
    return v === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

/**
 * Hidrata o tema na carga inicial. Chamar de main.jsx antes do render.
 *
 * Migração 13/05/2026: o admin shell sempre usa fundos dark, então default
 * vira "dark". Migração explícita: se localStorage tem "light" mas o
 * usuário ainda não confirmou via LpaThemeToggle (flag muuney_hub_theme_user_set),
 * fazemos override pra dark. Usuários que clicaram no toggle preservam escolha.
 */
export function hydrateTheme(): void {
  if (typeof document === "undefined") return;

  let theme: "light" | "dark" = "dark";
  try {
    const userSet = localStorage.getItem("lpa_theme_user_set") === "1";
    const stored = localStorage.getItem("lpa_theme");
    if (userSet && (stored === "light" || stored === "dark")) {
      theme = stored;
    } else {
      theme = "dark";
      localStorage.setItem("lpa_theme", "dark");
    }
  } catch {
    /* SSR/private mode */
  }
  document.documentElement.setAttribute("data-theme", theme);
}
